import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { GALLERY, GALLERY_FILTERS } from "../data/content";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";

export default function Gallery() {
  const [filter, setFilter] = useState("visi");

  const items = useMemo(
    () => (filter === "visi" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  return (
    <main data-testid="gallery-page">
      <Seo
        title="Atliktų darbų galerija — ART Ceiling"
        description="Tikri įtempiamų lubų darbai: matinės lubos, LED linijos, montavimo procesas. Nuotraukos iš objektų visoje Lietuvoje."
      />
      <section className="grain relative overflow-hidden bg-navy-950 pb-16 pt-32 lg:pb-24 lg:pt-44">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-300">Galerija</p>
            <h1 className="mt-4 max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Atlikti darbai visoje Lietuvoje
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  filter === f.id
                    ? "bg-navy-900 text-white shadow-lg"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {items.map((g) => (
                <motion.div
                  layout
                  key={g.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`gallery-item-${g.id}`}
                  className="group relative block overflow-hidden rounded-2xl border border-slate-200"
                >
                  <img
                    src={g.image_url}
                    alt={g.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-navy-950/80 via-transparent to-transparent p-5 opacity-0 transition-opacity duration-400 group-hover:opacity-100">
                    <span className="font-heading text-base font-semibold text-white">{g.title}</span>
                    <span className="mt-1 flex items-center gap-1.5 text-xs text-sky-200">
                      <MapPin className="h-3 w-3" />
                      {g.location}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
